import { sectionTextSubject } from "./types.js";
import { SURF_MAX_BULLETS_PER_SLIDE } from "../layout/designerHeuristics.js";
import { isKnownLayoutId, normalizeLayoutId } from "../layout/templates.js";
export { SURF_MAX_BULLETS_PER_SLIDE };
export function deriveSlideTextSubject(slide) {
    const title = slide.layers.find((l) => l.type === "text" && l.textRole === "title")?.content?.trim();
    const subtitle = slide.layers
        .find((l) => l.type === "text" && l.textRole === "subtitle")
        ?.content?.trim();
    const bullets = slide.layers
        .filter((l) => l.type === "text" && l.textRole === "bullet")
        .map((l) => l.content.trim())
        .filter(Boolean);
    const body = slide.layers.find((l) => l.type === "text" && l.textRole === "body")?.content?.trim();
    const parts = [
        slide.outlineVisual?.imageSubject,
        title,
        subtitle,
        ...bullets,
        body,
    ].filter((s) => Boolean(s?.trim()));
    return parts.join(". ").slice(0, 600) || "abstract concept";
}
export function imageRoleForOutlineSection(section) {
    const kind = section.visual?.kind ?? "none";
    if (kind === "none")
        return section.role === "cover" || section.role === "cta" ? "background" : "none";
    if (kind === "image" || kind === "graphic")
        return "content";
    if (kind === "chart" || kind === "table")
        return "background";
    return "background";
}
export function imageRoleForSlide(slide) {
    if (slide.visualBrief?.imageRole)
        return slide.visualBrief.imageRole;
    const kind = slide.outlineVisual?.kind ?? "none";
    if (kind === "image" || kind === "graphic")
        return "content";
    if (kind === "none") {
        const role = slide.outlineRole?.toLowerCase();
        if (role === "cover" || role === "cta" || role === "section" || role === "summary") {
            return "background";
        }
        return "none";
    }
    return "background";
}
export function visualBriefFromSection(section) {
    return {
        subject: sectionTextSubject(section),
        imageRole: imageRoleForOutlineSection(section),
        aspect: "16:9",
    };
}
export function outlineVisualToSlideRef(visual) {
    if (!visual)
        return undefined;
    return {
        kind: visual.kind,
        caption: visual.caption,
        imageSubject: visual.imageSubject,
        graphic: visual.graphic,
        chart: visual.chart,
        table: visual.table,
    };
}
export function placeholderLayerForVisual(visual, z) {
    void visual;
    void z;
    return null;
}
export function extractHighlightNumber(section) {
    const sources = [section.title, ...section.bullets, section.body ?? ""].join(" ");
    const match = sources.match(/\d[\d.,]*%?/);
    return match?.[0] ?? "—";
}
function cappedBullets(section) {
    return section.bullets.slice(0, SURF_MAX_BULLETS_PER_SLIDE);
}
function bulletBlock(section) {
    return cappedBullets(section)
        .map((b) => (b.trim().startsWith("•") ? b.trim() : `• ${b.trim()}`))
        .join("\n");
}
export function outlineLayoutForSection(section) {
    const hinted = section.layoutHint?.trim();
    if (hinted) {
        const normalized = normalizeLayoutId(hinted);
        if (isKnownLayoutId(normalized))
            return normalized;
    }
    switch (section.role) {
        case "cover":
            return "cover";
        case "section":
        case "summary":
            return "section";
        case "cta":
            return "closing";
        case "quote":
            return "quote";
        case "comparison":
            return "comparison";
        case "data":
            if (extractHighlightNumber(section) !== "—")
                return "big-number";
            return cappedBullets(section).length >= 2 ? "bullets" : "title-body";
        case "agenda":
            return "bullets";
        default:
            break;
    }
    if (section.visual?.kind === "image" || section.visual?.kind === "graphic") {
        return "image-left";
    }
    if (cappedBullets(section).length >= 2)
        return "bullets";
    if (section.body?.trim())
        return "title-body";
    return "bullets";
}
export function sectionToLayoutContents(section) {
    const layoutId = outlineLayoutForSection(section);
    const bullets = bulletBlock(section);
    const trimmedBullets = cappedBullets(section);
    switch (layoutId) {
        case "cover":
            return {
                title: section.title,
                subtitle: section.subtitle?.trim() || trimmedBullets[0] || "",
            };
        case "section":
            return {
                kicker: section.subtitle?.trim() || section.role.toUpperCase(),
                title: section.title,
            };
        case "closing":
            return {
                title: section.title,
                cta: section.subtitle?.trim() || "Scopri di più",
                body: section.body?.trim() || bullets,
            };
        case "quote":
            return {
                quote: section.body?.trim() || bullets || section.title,
                attribution: section.subtitle?.trim() || "—",
            };
        case "big-number":
        case "kpi":
            return {
                number: extractHighlightNumber(section),
                label: section.title,
                context: section.body?.trim() || trimmedBullets[0] || "",
            };
        case "comparison":
            return {
                title: section.title,
                option_a: trimmedBullets[0] ?? "Opzione A",
                option_b: trimmedBullets[1] ?? section.body?.trim() ?? "Opzione B",
            };
        case "bullets":
            return { title: section.title, bullets };
        case "two-column": {
            const mid = Math.ceil(trimmedBullets.length / 2) || 1;
            return {
                title: section.title,
                left: trimmedBullets.slice(0, mid).join("\n") || section.body?.trim() || "",
                right: trimmedBullets.slice(mid).join("\n") || "",
            };
        }
        case "image-left":
            return {
                title: section.title,
                body: section.body?.trim() || bullets,
                image: "",
            };
        default:
            return {
                title: section.title,
                body: section.body?.trim() || bullets,
            };
    }
}
/** @deprecated Use outlineLayoutForSection */
export const pickLayout = outlineLayoutForSection;
